import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    console.log("Quick!")
    this.initVoiceRecording()
    this.initMobileSearch()
  }

  initMobileSearch() {
    const mobileSearchInput = document.getElementById('search-patient-mobile')
    if (mobileSearchInput) {
      mobileSearchInput.addEventListener('input', (e) => {
        this.filterPatientsList(e.target.value.trim())
      })
    }
  }

  filterPatientsList(searchTerm) {
    const patientItems = document.querySelectorAll('.mobile-patient-item')
    
    if (!searchTerm) {
      // Show all patients if search is empty - maintain original visibility
      patientItems.forEach(item => {
        item.style.removeProperty('display')
        item.classList.remove('search-hidden')
      })
      return
    }
    
    const searchLower = searchTerm.toLowerCase()
    
    patientItems.forEach(item => {
      const patientNameElement = item.querySelector('h6')
      const patientInfoElement = item.querySelector('.text-muted')
      
      if (patientNameElement && patientInfoElement) {
        const patientName = patientNameElement.textContent.trim()
        const patientInfo = patientInfoElement.textContent.toLowerCase()
        
        // Check if search matches initials or word beginnings in name
        const nameMatches = this.matchesNameSearch(patientName, searchLower)
        // Check if search matches patient ID or other info
        const infoMatches = patientInfo.includes(searchLower)
        
        if (nameMatches || infoMatches) {
          item.style.removeProperty('display')
          item.classList.remove('search-hidden')
        } else {
          item.style.display = 'none'
          item.classList.add('search-hidden')
        }
      }
    })
  }

  matchesNameSearch(fullName, searchTerm) {
    const nameLower = fullName.toLowerCase()
    const words = nameLower.split(/\s+/).filter(word => word.length > 0)
    
    // Check if any word starts with the search term
    const startsWithMatch = words.some(word => word.startsWith(searchTerm))
    
    // Check if initials match (e.g., "bn" matches "B N Sharma")
    const initials = words.map(word => word.charAt(0)).join('')
    const initialsMatch = initials.startsWith(searchTerm)
    
    return startsWithMatch || initialsMatch
  }

  initVoiceRecording() {
    this.mediaRecorder = null
    this.audioChunks = []
    this.startTime = null
    this.recordingInterval = null

    // Use setTimeout to ensure DOM is ready
    setTimeout(() => {
      // Bind event handlers
      const startBtn = document.getElementById('startRecording')
      const stopBtn = document.getElementById('stopRecording')
      const retryBtn = document.getElementById('retryRecording')
      const submitBtn = document.getElementById('submitRecording')
      const closeMobileForm = document.getElementById('closeMobileForm')
      
      console.log('Init voice recording - buttons found:', {
        start: !!startBtn,
        stop: !!stopBtn,
        retry: !!retryBtn,
        submit: !!submitBtn,
        closeMobile: !!closeMobileForm
      })
      
      if (startBtn) startBtn.addEventListener('click', () => this.startRecording())
      if (stopBtn) stopBtn.addEventListener('click', () => this.stopRecording())
      if (retryBtn) retryBtn.addEventListener('click', () => this.retryRecording())
      if (submitBtn) submitBtn.addEventListener('click', () => this.submitRecording())
      if (closeMobileForm) closeMobileForm.addEventListener('click', () => this.closeMobileForm())

      // Reset modal when it's shown
      $('#voiceRecordingModal').on('shown.bs.modal', () => this.resetModal())
    }, 100)
  }

  async startRecording() {
    try {
      console.log('Starting audio recording...')
      
      // Check if MediaRecorder is supported
      if (!window.MediaRecorder) {
        alert('MediaRecorder is not supported in this browser. Please use Chrome, Firefox, or Safari.')
        return
      }

      const constraints = { 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      }
      
      console.log('Requesting microphone access with constraints:', constraints)
      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      
      console.log('Got audio stream:', {
        active: stream.active,
        tracks: stream.getAudioTracks().length,
        trackSettings: stream.getAudioTracks()[0]?.getSettings()
      })

      // Check supported MIME types and use the best one
      let mimeType = 'audio/webm'
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus'
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm'
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4'
      } else if (MediaRecorder.isTypeSupported('audio/wav')) {
        mimeType = 'audio/wav'
      }
      
      console.log('Using MIME type:', mimeType)

      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType,
        audioBitsPerSecond: 128000
      })
      this.audioChunks = []

      this.mediaRecorder.ondataavailable = (event) => {
        console.log('Audio data available:', {
          size: event.data.size,
          type: event.data.type
        })
        
        if (event.data.size > 0) {
          this.audioChunks.push(event.data)
        }
      }

      this.mediaRecorder.onstop = () => {
        console.log('Recording stopped. Total chunks:', this.audioChunks.length)
        console.log('Chunk sizes:', this.audioChunks.map(chunk => chunk.size))
        
        stream.getTracks().forEach(track => {
          console.log('Stopping track:', track.label)
          track.stop()
        })
        this.showCompletedState()
      }

      this.mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error)
        alert('Recording error: ' + event.error)
      }

      this.mediaRecorder.start(1000) // Request data every second
      this.startTime = Date.now()
      this.showRecordingState()
      this.startTimer()
      
      console.log('MediaRecorder started with state:', this.mediaRecorder.state)

    } catch (error) {
      console.error('Error accessing microphone:', error)
      alert('Could not access microphone. Please check permissions and try again.')
    }
  }

  stopRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop()
      this.stopTimer()
    }
  }

  retryRecording() {
    this.resetModal()
  }

  async submitRecording() {
    const appointmentData = this.getCurrentAppointmentData()
    
    if (!appointmentData || !appointmentData.appointmentId) {
      alert('No appointment found for the selected patient.')
      return
    }

    const { patientId, appointmentId } = appointmentData

    if (this.audioChunks.length === 0) {
      alert('No recording found.')
      return
    }

    // Create audio blob using the same MIME type as recording
    const mimeType = this.audioChunks[0]?.type || 'audio/webm'
    const audioBlob = new Blob(this.audioChunks, { type: mimeType })
    
    // Validate audio blob size (should be at least 1KB for meaningful audio)
    if (audioBlob.size < 1000) {
      alert('Recording is too short or empty. Please record for at least a few seconds.')
      document.getElementById('submitRecording').innerHTML = '<i class="fa fa-check mr-2"></i>Submit'
      document.getElementById('submitRecording').disabled = false
      return
    }
    
    const formData = new FormData()
    formData.append('voice[audio_file]', audioBlob, `voice_${appointmentId}_${Date.now()}.wav`)
    formData.append('voice[appointment_id]', appointmentId)

    // Show loading state
    document.getElementById('submitRecording').innerHTML = '<i class="fa fa-spinner fa-spin mr-2"></i>Submitting...'
    document.getElementById('submitRecording').disabled = true

    try {
      console.log('Voice Recording Debug:', {
        url: `${HOST}/api/v1/voices`,
        appointmentId: appointmentId,
        patientId: patientId,
        token: TOKEN ? 'Present' : 'Missing',
        audioSize: audioBlob.size,
        csrfToken: document.querySelector('[name="csrf-token"]') ? 'Present' : 'Missing'
      })
      
      const response = await fetch(`${HOST}/api/v1/voices`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${TOKEN}`,
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: formData
      })
      
      console.log('Voice Recording Response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      })

      const result = await response.json()
      console.log('Voice Recording Result:', result)

      if (response.ok && result.status.status === 'SUCCESS') {
        const message = result.payload.ai_summary_generated 
          ? 'Voice recording saved and AI summary generated successfully!'
          : 'Voice recording saved (AI summary generation failed)'
        
        if (typeof flashToast !== 'undefined') {
          flashToast(message, 'success')
        } else {
          alert(message)
        }
        $('#voiceRecordingModal').modal('hide')
        
        // Refresh the voice recording section to show the new recording
        this.checkExistingVoiceRecording(appointmentData.appointmentId)
      } else {
        throw new Error(result.status?.message || 'Failed to save recording')
      }
    } catch (error) {
      console.error('Error submitting recording:', error)
      const errorMessage = error.message || 'Failed to save recording. Please try again.'
      
      if (typeof flashToast !== 'undefined') {
        flashToast(errorMessage, 'error')
      } else {
        alert(errorMessage)
      }
    } finally {
      document.getElementById('submitRecording').innerHTML = '<i class="fa fa-check mr-2"></i>Submit'
      document.getElementById('submitRecording').disabled = false
    }
  }

  showRecordingState() {
    document.getElementById('readyState').classList.add('d-none')
    document.getElementById('recordingState').classList.remove('d-none')
    document.getElementById('completedState').classList.add('d-none')
    
    document.getElementById('readyButtons').classList.add('d-none')
    document.getElementById('recordingButtons').classList.remove('d-none')
    document.getElementById('completedButtons').classList.add('d-none')
  }

  showCompletedState() {
    const duration = this.formatDuration(Date.now() - this.startTime)
    document.getElementById('finalDuration').textContent = duration

    // Create audio blob for preview using the same MIME type as recording
    const mimeType = this.audioChunks[0]?.type || 'audio/webm'
    const audioBlob = new Blob(this.audioChunks, { type: mimeType })
    const audioUrl = URL.createObjectURL(audioBlob)
    
    console.log('Creating audio blob for preview:', {
      mimeType: mimeType,
      blobSize: audioBlob.size,
      chunks: this.audioChunks.length
    })
    
    // Set up audio player for review
    const audioPlayer = document.getElementById('recordingPreview')
    audioPlayer.src = audioUrl
    
    // Clean up old URL when modal is hidden
    audioPlayer.addEventListener('loadstart', () => {
      // Store the URL for cleanup
      audioPlayer.dataset.audioUrl = audioUrl
    })

    // Show patient info
    const appointmentData = this.getCurrentAppointmentData()
    if (appointmentData) {
      document.getElementById('selectedPatientName').textContent = appointmentData.patientName
      document.getElementById('patientInfo').classList.remove('d-none')
    }

    document.getElementById('readyState').classList.add('d-none')
    document.getElementById('recordingState').classList.add('d-none')
    document.getElementById('completedState').classList.remove('d-none')
    
    document.getElementById('readyButtons').classList.add('d-none')
    document.getElementById('recordingButtons').classList.add('d-none')
    document.getElementById('completedButtons').classList.remove('d-none')
  }

  resetModal() {
    // Clean up audio URL to prevent memory leaks
    const audioPlayer = document.getElementById('recordingPreview')
    if (audioPlayer && audioPlayer.src && audioPlayer.src.startsWith('blob:')) {
      URL.revokeObjectURL(audioPlayer.src)
      audioPlayer.src = ''
    }
    
    document.getElementById('readyState').classList.remove('d-none')
    document.getElementById('recordingState').classList.add('d-none')
    document.getElementById('completedState').classList.add('d-none')
    document.getElementById('patientInfo').classList.add('d-none')
    
    document.getElementById('readyButtons').classList.remove('d-none')
    document.getElementById('recordingButtons').classList.add('d-none')
    document.getElementById('completedButtons').classList.add('d-none')

    document.getElementById('recordingTimer').textContent = '00:00'
    this.audioChunks = []
    this.stopTimer()
  }

  startTimer() {
    this.recordingInterval = setInterval(() => {
      const elapsed = Date.now() - this.startTime
      document.getElementById('recordingTimer').textContent = this.formatDuration(elapsed)
    }, 1000)
  }

  stopTimer() {
    if (this.recordingInterval) {
      clearInterval(this.recordingInterval)
      this.recordingInterval = null
    }
  }

  formatDuration(ms) {
    const seconds = Math.floor(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  showVoiceRecordingButton(patientData) {
    console.log('showVoiceRecordingButton called with:', patientData)
    
    // Try both methods to find the button
    const voiceBtn = document.getElementById('voiceRecordBtn')
    const $voiceBtn = $('#voiceRecordBtn')
    
    console.log('Voice button found via document.getElementById:', voiceBtn)
    console.log('Voice button found via jQuery:', $voiceBtn.length)
    
    if (patientData && patientData.full_name) {
      console.log('Showing voice button for:', patientData.full_name)
      
      // Store patient info for later use
      window.selectedPatientData = patientData
      
      // Try showing with both methods
      if (voiceBtn) {
        voiceBtn.classList.remove('d-none')
      }
      if ($voiceBtn.length > 0) {
        $voiceBtn.removeClass('d-none')
      }
      
      console.log('Button should now be visible')
    } else {
      console.log('Button not shown - patientData:', !!patientData, 'full_name:', patientData?.full_name)
    }
  }

  getCurrentAppointmentData() {
    // Get the current appointment data from the form and selected patient
    const patientData = window.selectedPatientData
    if (!patientData) return null

    // Find the latest paid appointment for this patient
    const latestAppointment = patientData.appointments.find(apt => apt.status === "paid")
    
    return {
      patientId: patientData.id,
      appointmentId: latestAppointment ? latestAppointment.id : null,
      patientName: patientData.full_name
    }
  }

  closeMobileForm() {
    $('#mobileRecordSection').addClass('d-none')
    $('#mobilePatientForm').addClass('d-none')
    $('#mobileInvestigations').addClass('d-none')
    
    // Clear stored data
    window.selectedPatientData = null
    window.selectedAppointmentId = null
    
    // Scroll back to top
    $('html, body').animate({
      scrollTop: 0
    }, 500);
  }


  fillPatientMobile(event) {
    var block = this;
    var patientId = $(event.currentTarget).attr('data-patientid');
    var appointmentId = $(event.currentTarget).attr('data-appointmentid');
    
    console.log('Mobile patient selected:', patientId, appointmentId)
    
    $.ajax({
      url: `${HOST}/api/v1/patients/${patientId}`, 
      headers: { "Authorization": `Token ${TOKEN}` },
      success: function(res){
        var patientParams = res.payload
        
        // Fill mobile form
        block.fillMobilePatientData(patientParams)
        
        // Show voice recording button
        block.showMobileVoiceRecordingButton(patientParams, appointmentId)
        
        // Show mobile form and investigations
        $('#mobilePatientForm').removeClass('d-none')
        $('#mobileInvestigations').removeClass('d-none')
        
        // Scroll to form
        $('html, body').animate({
          scrollTop: $("#mobileRecordSection").offset().top - 20
        }, 500);
        
        // Store appointment data
        window.selectedPatientData = patientParams
        window.selectedAppointmentId = appointmentId
        
        // Fill investigations for mobile
        block.fillInvestigationsMobile(appointmentId)
      },
      error: function(err){
        console.log(err)
        if (typeof flashToast !== 'undefined') {
          flashToast('Error loading patient data', 'error')
        }
      }
    })
  }

  fillMobilePatientData(patientParams) {
    // Fill mobile form fields
    $('input[name="patient[uid]"]').val(patientParams.uid || '')
    $('input[name="patient[full_name]"]').val(patientParams.full_name || '')
    $('input[name="patient[mobile]"]').val(patientParams.mobile || '')
    $('select[name="patient[gender]"]').val(patientParams.gender || '')
    
    // Fill appointment data if available
    var latestAppointment = patientParams.appointments.filter(m => m.status == "paid")[0]
    if (latestAppointment) {
      $('input[name="appointment[height]"]').val(latestAppointment.height || '')
      $('input[name="appointment[weight]"]').val(latestAppointment.weight || '')
      $('input[name="appointment[fever]"]').val(latestAppointment.fever || '')
      $('input[name="appointment[bp_s]"]').val(latestAppointment.bp_s || '')
      $('input[name="appointment[bp_d]"]').val(latestAppointment.bp_d || '')
      $('input[name="appointment[bmi]"]').val(latestAppointment.bmi || '')
      
      $('#mobile_smoking').prop('checked', latestAppointment.smoking || false)
      $('#mobile_tobacco').prop('checked', latestAppointment.tobacco || false)
      $('#mobile_alcohol').prop('checked', latestAppointment.alcohol || false)
    }
  }

  showMobileVoiceRecordingButton(patientData, appointmentId) {
    console.log('Showing mobile voice button for:', patientData.full_name, 'appointment:', appointmentId)
    
    if (patientData && patientData.full_name) {
      $('#selectedPatientNameMobile').text(patientData.full_name)
      $('#mobileRecordSection').removeClass('d-none')
      
      // Check if voice recording already exists for this specific appointment
      this.checkExistingVoiceRecording(appointmentId)
    }
  }

  checkExistingVoiceRecording(appointmentId) {
    if (!appointmentId) return
    
    console.log('Checking existing voice recording for appointment:', appointmentId)
    
    $.ajax({
      url: `${HOST}/api/v1/voices/${appointmentId}`,
      headers: { "Authorization": `Token ${TOKEN}` },
      method: 'GET',
      success: (result) => {
        console.log('Existing voice check result:', result)
        if (result.status.status === 'SUCCESS') {
          // Show existing recording UI
          this.showExistingRecordingUI(result.payload)
        } else {
          // Show record button
          this.showRecordButtonUI()
        }
      },
      error: (xhr) => {
        console.log('No existing recording found or error:', xhr.status)
        // Show record button
        this.showRecordButtonUI()
      }
    })
  }

  showExistingRecordingUI(voiceData) {
    const recordSection = $('#mobileRecordSection')
    const audioUrl = voiceData.audio_url
    const createdAt = new Date(voiceData.created_at).toLocaleDateString()
    const aiSummary = voiceData.ai_summary
    
    recordSection.html(`
      <div class="bg-light border rounded p-3">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="text-success m-0">
            <i class="fa fa-check-circle mr-2"></i>${window.selectedPatientData?.full_name || 'Voice Recording Available'}
          </h6>
          <button type="button" class="btn btn-outline-primary btn-sm" data-toggle="modal" data-target="#voiceControlsModal" data-voice-data='${JSON.stringify(voiceData)}'>
            <i class="fa fa-cog mr-2"></i>Controls
          </button>
        </div>
        
        <p class="text-muted small mb-2">Recorded on ${createdAt}</p>
        
        ${audioUrl ? `
        <div class="mb-3">
          <audio controls class="w-100">
            <source src="${audioUrl}" type="audio/wav">
            Your browser does not support the audio element.
          </audio>
        </div>
        ` : ''}
        
        ${aiSummary ? this.renderAISummary(aiSummary) : '<p class="text-muted"><em>No AI summary generated</em></p>'}
      </div>
    `)
    
    // Add event listener for controls modal
    recordSection.find('[data-toggle="modal"][data-target="#voiceControlsModal"]').on('click', (e) => {
      const voiceData = JSON.parse($(e.currentTarget).attr('data-voice-data'))
      this.showVoiceControlsModal(voiceData)
    })
  }

  renderAISummary(aiSummary) {
    if (!aiSummary || !aiSummary.clinical_summary) {
      return '<p class="text-muted"><em>No AI summary available</em></p>'
    }

    const clinical = aiSummary.clinical_summary
    const diagnosis = aiSummary.diagnosis || {}
    
    // Check if this is essentially an empty summary (all null/empty values)
    const hasChiefComplaint = clinical.chief_complaint && clinical.chief_complaint !== 'null' && clinical.chief_complaint.trim() !== ''
    const hasHistory = clinical.history_of_present_illness && clinical.history_of_present_illness !== 'null' && clinical.history_of_present_illness.trim() !== ''
    const hasDiagnosis = diagnosis.provisional_diagnosis && diagnosis.provisional_diagnosis !== 'null' && diagnosis.provisional_diagnosis.trim() !== ''
    const hasSymptoms = diagnosis.identified_symptoms && diagnosis.identified_symptoms.length > 0
    
    if (!hasChiefComplaint && !hasHistory && !hasDiagnosis && !hasSymptoms) {
      return `
        <div class="mt-3">
          <h6 class="text-warning mb-3">
            <i class="fa fa-exclamation-triangle mr-2"></i>AI Summary
          </h6>
          <div class="alert alert-warning">
            <p class="mb-0 small">
              <strong>No medical conversation detected.</strong><br>
              The audio may contain non-medical content, background noise, or unclear speech. 
              Please record a clear doctor-patient medical conversation for accurate summarization.
            </p>
          </div>
        </div>
      `
    }
    
    return `
      <div class="mt-3">
        <h6 class="text-primary mb-3">
          AI Summary
        </h6>
        
        <div class="row">
          <div class="col-12 mb-3">
            <h6 class="text-secondary mb-2">Chief Complaint:</h6>
            <p class="mb-0 small">${clinical.chief_complaint || 'Not specified'}</p>
          </div>
          
          <div class="col-12 mb-3">
            <h6 class="text-secondary mb-2">History of Present Illness:</h6>
            <ul class="list-unstyled small mb-0">
              ${clinical.history_of_present_illness ? 
                clinical.history_of_present_illness.split(/[.\n]/).filter(item => item.trim()).map(item => 
                  `<li class="mb-1">• ${item.trim()}</li>`
                ).join('') 
                : '<li><em>Not specified</em></li>'}
            </ul>
          </div>
          
          ${diagnosis.provisional_diagnosis ? `
          <div class="col-12 mb-3">
            <h6 class="text-secondary mb-2">Provisional Diagnosis:</h6>
            <p class="mb-0 small">${diagnosis.provisional_diagnosis}</p>
          </div>
          ` : ''}
          
          ${diagnosis.identified_symptoms && diagnosis.identified_symptoms.length > 0 ? `
          <div class="col-12 mb-3">
            <h6 class="text-secondary mb-2">Identified Symptoms:</h6>
            <div class="mb-0">
              ${diagnosis.identified_symptoms.map(symptom => 
                `<span class="badge badge-secondary mr-1 mb-1">${symptom}</span>`
              ).join('')}
            </div>
          </div>
          ` : ''}
        </div>
      </div>
    `
  }

  showVoiceControlsModal(voiceData) {
    const modalContent = $('#voiceControlsContent')
    const audioUrl = voiceData.audio_url
    const createdAt = new Date(voiceData.created_at).toLocaleDateString()
    
    modalContent.html(`
      <div class="text-center">
        <p class="text-muted mb-3">Recorded on ${createdAt}</p>
        
        ${audioUrl ? `
        <div class="mb-4">
          <audio controls class="w-100">
            <source src="${audioUrl}" type="audio/wav">
            Your browser does not support the audio element.
          </audio>
        </div>
        ` : ''}
        
        <div class="btn-group d-block">
          <button type="button" class="btn btn-outline-danger mr-2" data-voice-id="${voiceData.voice_id}" data-action="delete-recording">
            <i class="fa fa-trash mr-2"></i>Delete Recording
          </button>
          
          <button type="button" class="btn btn-outline-secondary" data-action="show-record-button">
            <i class="fa fa-plus mr-2"></i>Record New
          </button>
        </div>
      </div>
    `)
    
    // Add event listeners for the modal buttons
    modalContent.find('[data-action="delete-recording"]').on('click', (e) => {
      const voiceId = $(e.currentTarget).data('voice-id')
      $('#voiceControlsModal').modal('hide')
      this.deleteVoiceRecording(voiceId)
    })
    
    modalContent.find('[data-action="show-record-button"]').on('click', () => {
      $('#voiceControlsModal').modal('hide')
      this.showRecordButtonUI()
    })
  }

  showRecordButtonUI() {
    const recordSection = $('#mobileRecordSection')
    recordSection.html(`
      <div class="text-center">
        <button type="button" class="btn btn-danger btn-lg pulse-animation" data-toggle="modal" data-target="#voiceRecordingModal">
          <i class="fa fa-microphone mr-2"></i>Record Voice for <span id="selectedPatientNameMobile"></span>
        </button>
      </div>
    `)
    
    // Restore patient name
    if (window.selectedPatientData && window.selectedPatientData.full_name) {
      $('#selectedPatientNameMobile').text(window.selectedPatientData.full_name)
    }
  }

  deleteVoiceRecording(voiceId) {
    if (!confirm('Are you sure you want to delete this voice recording?')) {
      return
    }
    
    console.log('Deleting voice recording:', voiceId)
    
    $.ajax({
      url: `${HOST}/api/v1/voices/${voiceId}`,
      headers: { "Authorization": `Token ${TOKEN}` },
      method: 'DELETE',
      success: (result) => {
        console.log('Delete result:', result)
        if (result.status.status === 'SUCCESS') {
          // Show record button again
          this.showRecordButtonUI()
          if (typeof flashToast !== 'undefined') {
            flashToast('Voice recording deleted successfully', 'success')
          }
        }
      },
      error: (xhr) => {
        console.error('Delete error:', xhr)
        if (typeof flashToast !== 'undefined') {
          flashToast('Failed to delete voice recording', 'error')
        }
      }
    })
  }

  fillInvestigationsMobile(appointmentId) {
    $.ajax({
      url: `${HOST}/api/v1/appointments/${appointmentId}`, 
      headers: { "Authorization": `Token ${TOKEN}` },
      success: function(appt){
        var medParams = appt.payload.quick_investigations
        $('.investigations-mobile').html('')
        $('.save-investigations-mobile').addClass("d-none")
        
        for (let index = 0; index < medParams.length; index++) {
          var obj = medParams[index];
          var val = obj && obj.value ? obj.value : '';
          
          let fields = `
            <div class="col-6 mt-2 investigation-fields-mobile">
              <label class="small font-weight-bold text-muted"> ${obj.name.toUpperCase()} </label>
              <input type="text" name="value" id="mobile_${obj.name.toLowerCase()}" class="form-control form-control-sm" placeholder="${obj.name}" value="${val}">
            </div>`;
          $('.investigations-mobile').append(fields)
        }
        
        $('.save-investigations-mobile').removeClass("d-none")
        $('.save-investigations-mobile').attr("data-appointment-id", appointmentId)
      },
      error: function(err){
        console.log(err)
      }
    })
  }

  saveAppointmentMobile(event) {
    var appointmentId = window.selectedAppointmentId
    var patientId = window.selectedPatientData?.id
    
    if (!appointmentId || !patientId) {
      alert('Please select a patient first')
      return
    }
    
    // Same logic as desktop version but for mobile form
    var appointmentParams = ["height", "weight", "fever", "bp_s", "bp_d", "bmi", "tobacco", "alcohol", "smoking"]
    var objAppt = { state: "red" }
    
    appointmentParams.forEach(param => {
      if (param === "tobacco" || param === "alcohol" || param === "smoking") {
        objAppt[param] = $(`#mobile_${param}`).prop('checked')
      } else {
        var val = $(`input[name="appointment[${param}]"]`).val()
        if (val) objAppt[param] = val
      }
    })
    
    // Show loading
    $(event.target).html('<i class="fa fa-spinner fa-spin mr-2"></i>Saving...')
    $(event.target).prop('disabled', true)
    
    $.ajax({
      url: `${HOST}/api/v1/appointments/${appointmentId}`,
      headers: { "Authorization": `Token ${TOKEN}` },
      data: { appointment: objAppt },
      method: "PUT",
      success: function(pp){
        if (typeof flashToast !== 'undefined') {
          flashToast("Successfully Saved", "success")
        }
        
        // Update patient data
        var patientData = {
          full_name: $('input[name="patient[full_name]"]').val(),
        }
        
        $.ajax({
          url: `${HOST}/api/v1/patients/${patientId}`,
          headers: { "Authorization": `Token ${TOKEN}` },
          data: { patient: patientData },
          method: "PUT",
          success: function(res){
            console.log('Patient updated')
          }
        })
      },
      error: function(err){
        if (typeof flashToast !== 'undefined') {
          flashToast("Error saving data", "error")
        }
      },
      complete: function(){
        $(event.target).html('<i class="fa fa-save mr-2"></i>Update Patient Info')
        $(event.target).prop('disabled', false)
      }
    })
  }

  fillInvestigations(event){
    if ($(event.target).length == 0){
      var $el = event
    }else{
      var $el = $(event.target)
    }
    var appointmentId = $el.attr("data-appointmentid");

    if (appointmentId){
      $.ajax({
        url: `${HOST}/api/v1/appointments/${appointmentId}`, 
        headers: { "Authorization": `Token ${TOKEN}` },
        success: function(appt){

          // Fill investigation value
          var medParams = appt.payload.quick_investigations
          $(`.investigations`).html(null)
          $(`.investigations`).next('a').addClass("d-none")
          console.log("Medparams", medParams)
          
          for (let index = 0; index < medParams.length; index++) {
            var obj = medParams[index];
            var val = '';

            if (obj && obj.value){ val = obj.value } 
            let fields = `
                <div class="col-3 mt-2 investigation-fields">
                  <label class="display-7"> ${obj.name.toUpperCase()} </label>
                  <div class="d-flex">
                    <div class="small-field">
                      <input type="text" name="value" id="${obj.name.toLowerCase()}" class="form-control" placeholder="${obj.name.toLocaleUpperCase()}" value="${val}">
                      <small class="text-center d-block"></small>
                    </div>
                  </div>
                </div>`;
              $(`.investigations`).append(fields)
              $(`.investigations`).next('a').removeClass("d-none")
          }
          $(`.investigations`).next('a').attr("data-appointmentId", appointmentId)
        },
        error: function(err){
          console.log(err)
          flashToast(err, "warning")
        }
      })
    }
  }

  saveInvestigation(event){
    var $el = $(event.target)
    var data = {}
    $(".investigations input").each(function(){
      data[$(this).attr("id")] = $(this).val()
    })

    $(".save-investigations").addClass("d-none")
    
    $.ajax({ 
      url:`/api/v1/medical_test_reports/investigations`,
      headers: { "Authorization": `Token ${TOKEN}` },
      method: "POST",
      data: {
        reports: data,
        appointment_id: $(`.investigations`).next('a').attr("data-appointmentId"),
        state: "green",
        patient_id: $el.attr(`data-patientid`)
      },
      success: function(res){
        console.log(res)
        for (let index = 0; index < res.payload.length; index++) {
          const element = res.payload[index];
          $(`.investigation-fields #${element.name}`).attr("data-reportid", element.value)
        }
        $(".save-investigations").removeClass("d-none")
        flashToast("Report Value Saved", "info")
        $(`#${$el.attr('data-patientid')}`).addClass('bg-success');
      },
      error: function(err){
        console.log(err)
        flashToast("Test Not Exist", "warning")
      }
    });
  }

  fillPatient(event){
    var block = this;
    var patientId = $(event.target).attr('data-patientId');
    $.ajax({
      url: `${HOST}/api/v1/patients/${patientId}`, 
      headers: { "Authorization": `Token ${TOKEN}` },
      success: function(res){
        var patientParams = res.payload
        fillPatientData(patientParams)
        $(".appointment-list .card-body").html(table)
        
        // Show voice recording button when patient is selected
        console.log('Patient selected:', patientParams.full_name)
        block.showVoiceRecordingButton(patientParams)

        // Check for AI summary and show popup
        block.checkAISummary(patientId)
        
        var latestAppointment = res.payload.appointments.filter(m => m.status == "paid")[0]
        for (const k in latestAppointment) {
          var apptVal = latestAppointment[k] || "";
          var $apptElement = $(`#appointment_${k}`)
          
          // Appointment Fields
          if(!$($apptElement).is("select")){
            $(`input[name="appointment[${k}]"]`).val(apptVal)
          }else if ($apptElement.is(":checkbox")){
            $(`#appointment_${k}`).prop('checked', apptVal);
          }else if(k.indexOf("_id") == -1){
            $(`button[data-id='appointment_${k}']`).trigger("click")
            $(`button[data-id='appointment_${k}']`)
              .next(".dropdown-menu")
              .find(`span:contains(${ humanize(apptVal) })`)
              .parent().trigger("click")
          }else{
            $(`#appointment_${k}`).val(apptVal).trigger('change')
            // selectDropdownById(`appointment_${k}`, apptVal)
          }

          if (["tobacco", "alcohol", "smoking"].indexOf(k) != -1){
            $(`#appointment_${k}`).prop('checked', apptVal);
          }
        }

        console.log(patientParams)

        $("#appointment_doctor_id").attr("disabled", true)
        $('#appointment_doctor_id').selectpicker('refresh');

        $("#appointment_consultant_id").attr("disabled", true)
        $('#appointment_consultant_id').selectpicker('refresh');
        $('#appointment_visit_date').val(latestAppointment?.visit_date);

        $("#appointment_referrer_id").val(latestAppointment?.referrer_id).trigger("change")

        var slotId = latestAppointment?.appointment_slot_id;
        if (slotId){
          $("#appointment_appointment_slot_id").html(`<option selected=true value="${slotId}">
            ${latestAppointment.visit_time}
          </option>`)
          $("#appointment_appointment_slot_id").attr("disabled", true)
        }
      
        const appnts = res.payload.appointments
        var tr = '';

        for (let index = 0; index < appnts.length; index++) {
          const appointment = appnts[index];
          tr += `<tr id="visit-${appointment.id}" class="selected-patient-appointments" data-appointmentid="${appointment.id}" data-action="click->quick#fillInvestigations"> 
            <td class="pl-1"> ${appointment.status} </td> 
            <td> ${appointment.id || ''} </td> 
            <td class="display-12"> 
              <small>${parseDate(appointment.visit_date)}   ${appointment.visit_time || '<i class="badge badge-sm badge-warning">waiting</i>'} </small> 
            </td> 
          </tr>`
        }

        let table = `<table class="table table-hover">
            <thead>
              <tr>
                <th scope="col">Status</th>
                <th scope="col">Appointment ID</th>
                <th scope="col display-1">Visit Time</th>
              </tr>
            </thead>
            <tbody> ${tr} </tbody>
          </table>
        `;
        $(".appointment-list").removeClass("d-none")
        $(".appointment-list .card-body").html(table)
        
        $(".investigations").next("a").attr("data-patientid", patientId);
        $(".quick-save").attr("data-patientid", patientParams.id)
        if (latestAppointment) {
          var el = $(`#visit-${latestAppointment.id}`)
          $(".quick-save").attr("data-appointmentid", latestAppointment.id)
          block.fillInvestigations(el)
        }
      }
    })
  }



  // Update Appointment
  saveAppointment(event){

    var appointmentParams = [
      "referrer_id",
      "height",
      "weight",
      "fever",
      "bp_s",
      "bp_d",
      "bmi",
      "tobacco",
      "alcohol",
      "smoking"
    ]

    var appointmentId = $(event.target).attr("data-appointmentid");
    var patientid = $(event.target).attr("data-patientid");
    
    var objAppt = { state: "red" }
    for (let i = 0; i < appointmentParams.length; i++) {
      var element = appointmentParams[i];
      if ($(`#appointment_${element}`).val()) {
        objAppt[element] = $(`#appointment_${element}`).val()
      }else if ( ["tobacco","alcohol","smoking"].indexOf(element) != -1 ){
        objAppt[element] = $(`#appointment_${element}`).prop('checked')
      }
    }

    $.ajax({
      url: `${HOST}/api/v1/appointments/${appointmentId}`,
      headers: { "Authorization": `Token ${TOKEN}` },
      data: { appointment: objAppt },
      method: "PUT",
      success: function(pp){
        console.log(pp)
        const $el = $(".doctor-appt-table").find(`tr[data-appointnmentid='${pp.id}']`)

        if ($el.length > 0){
          $(`#${$el}`).addClass('bg-danger');
        }
        flashToast("Successfully Saved", "info")
      },
      error: function(err, obj){
        try {
          var msg = err.responseJSON.status.message
        }catch {
          var msg = "Failed. Please fill correct appointment data"
        }
        flashToast(msg, "warning");
      } 
    })


    $.ajax({
      url: `${HOST}/api/v1/patients/${patientid}`,
      headers: { "Authorization": `Token ${TOKEN}` },
      data: { patient: {
        full_name: $("#patient_full_name").val(),
        diet: $("#patient_diet").val()
      } },
      method: "PUT",
      success: function(res){
        console.log(res)
        flashToast("Updated Successfully", "info")
      },
      error: function(err){
        console.log(err)
        flashToast(err.responseJSON.status.message, "warning")
      } 
    })

    return false;
  };


  referrerForm(event){
    console.log(event)
    var referrerId = $(event.target).closest("a").attr("data-referrer_id");

    if (!referrerId){
      referrerId = $("#appointment_referrer_id").val() 
    }
    $.get(`/quick_check/referrer_form?referrer_id=${referrerId}`)
  }

  checkAISummary(patientId) {
    const block = this;
    const HOST = window.location.origin;
    const TOKEN = window.TOKEN || window.readCookie("_priac_jwt");

    console.log('checkAISummary called with patientId:', patientId);

    $.ajax({
      url: `${HOST}/quick_check/ai_summary?patient_id=${patientId}`,
      headers: { "Authorization": `Token ${TOKEN}` },
      method: 'GET',
      success: function(summaryData) {
        console.log('AI summary response:', summaryData);
        console.log('Has keys:', Object.keys(summaryData || {}));
        if (summaryData && (summaryData.clinical_summary || summaryData.chiefComplaints || summaryData.clinicalStatus || summaryData.categorySummary || summaryData.clinicalImpression || summaryData.differentialDiagnosis)) {
          console.log('AI summary has content, fetching patient data...');
          const pData = window.selectedPatientData || {
            full_name: $('input[name="patient[full_name]"]').val(),
            uid: $('#patient_uid').val() || $('input[name="patient[uid]"]').val()
          };
          block.showAISummaryModal(summaryData, pData, patientId);
        } else {
          console.log('No AI summary content found');
          $('#printAiSummarySection').addClass('d-none');
        }
      },
      error: function(err) {
        console.error('AI summary fetch error:', err);
        $('#printAiSummarySection').addClass('d-none');
      }
    });
  }

  showAISummaryModal(summaryData, patientData, patientId) {
    const modalHtml = this.buildAISummaryModalHtml(summaryData, patientData);
    if (!modalHtml) return;

    $('#aiSummaryModal').remove();
    $('.modal-container').html(modalHtml);
    $('#aiSummaryModal').modal('show');

    // Show print button
    $('#printAiSummarySection').removeClass('d-none');
    $('#printAiSummaryBtn').attr('data-patient-id', patientId);

    // Store specialist notes data on the modal for print access
    const specialistNotesData = summaryData.specialistNotes || [];
    $('#aiSummaryModal').data('specialistNotes', specialistNotesData);

    // Handle print from popup — use event namespace to prevent double binding
    $('#aiSummaryModal .btn-print-summary').off('click.printSummary').on('click.printSummary', function(e) {
      e.stopPropagation();
      const notesData = $('#aiSummaryModal').data('specialistNotes') || [];

      // Collect only checked specialist notes by index (deduplicated)
      const selectedByIndex = {};
      $('#aiSummaryModal .specialist-note-print-check:checked').each(function() {
        const idx = parseInt($(this).data('index'));
        if (idx < notesData.length && !selectedByIndex[idx]) {
          selectedByIndex[idx] = { department: notesData[idx].department, note: notesData[idx].note };
        }
      });
      const selectedNotes = Object.values(selectedByIndex);

      $.ajax({
        url: `/quick_check/print_ai_summary?patient_id=${patientId}`,
        method: 'GET',
        data: { specialist_notes_override: JSON.stringify(selectedNotes) },
        success: function(data) { if (data.content) window.printPage(data.content); },
        error: function() { alert('Error printing summary'); }
      });
    });

    // Handle save
    const HOST = window.location.origin;
    $('#aiSummaryModal .btn-confirm').off('click').on('click', function() {
      const editedData = {};

      // Red flags
      const redFlagsVal = $('#ai-edit-red-flags').val();
      if (redFlagsVal !== undefined) {
        editedData.redFlagsDetected = redFlagsVal.split(';').map(s => s.trim()).filter(s => s);
      }

      const chiefComplaint = $('#ai-edit-chief-complaint').val();
      if (chiefComplaint !== undefined) editedData.chiefComplaints = chiefComplaint;
      const impression = $('#ai-edit-clinical-impression').val();
      if (impression !== undefined) editedData.clinicalImpression = impression;

      const clinicalStatus = {};
      $('.ai-edit-field[data-section="clinicalStatus"]').each(function() {
        clinicalStatus[$(this).data('key')] = $(this).val();
      });
      if (Object.keys(clinicalStatus).length > 0) editedData.clinicalStatus = clinicalStatus;

      const holistic = {};
      $('.ai-edit-field[data-section="holisticDashboard"]').each(function() {
        holistic[$(this).data('key')] = $(this).val();
      });
      if (Object.keys(holistic).length > 0) editedData.holisticDashboard = holistic;

      // Prefer appointment_id from summary data (authoritative)
      const todayApptId = summaryData._appointment_id || $('input[name="appointment[id]"]').val() || $('[data-appointmentid]').first().attr('data-appointmentid');

      // Without an appointment id we CANNOT save. Previously the code silently
      // skipped the request and still closed the modal, so the edit looked saved
      // but never persisted. Surface it and keep the modal open instead.
      if (!todayApptId) {
        if (typeof flashToast === 'function') flashToast('Could not save — no appointment selected. Reopen the patient and try again.', 'warning');
        return;
      }

      const $btn = $(this);
      $btn.prop('disabled', true);
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      $.ajax({
        url: `/quick_check/update_ai_summary`,
        method: 'PATCH',
        contentType: 'application/json',
        headers: { 'X-CSRF-Token': csrfToken },
        data: JSON.stringify({ appointment_id: todayApptId, ...editedData }),
        success: function() {
          if (typeof flashToast === 'function') flashToast('Summary saved', 'success');
          $('#aiSummaryModal').modal('hide');   // only close once the save is confirmed
        },
        error: function(err) {
          console.error('Failed to update AI summary:', err);
          var msg = (err.responseJSON && err.responseJSON.error) || 'Could not save the summary. Please try again.';
          if (typeof flashToast === 'function') flashToast(msg, 'warning');
          // keep the modal open so the edit isn't lost
        },
        complete: function() { $btn.prop('disabled', false); }
      });
    });
  }

  buildAISummaryModalHtml(summaryData, patientData) {
    const isOldFormat = !!summaryData.clinical_summary;
    const chiefComplaints = isOldFormat ? (summaryData.clinical_summary?.chief_complaint || '') : (summaryData.chiefComplaints || '');
    const clinicalImpression = summaryData.clinicalImpression || summaryData.differentialDiagnosis || '';
    const clinicalStatus = summaryData.clinicalStatus || summaryData.categorySummary || {};
    const holistic = summaryData.holisticDashboard || {};
    const redFlags = summaryData.redFlagsDetected || [];
    const specialistNotes = summaryData.specialistNotes || [];
    const referrals = summaryData.alliedReferrals || [];

    const hasContent = chiefComplaints || clinicalImpression || Object.keys(clinicalStatus).length > 0;
    if (!hasContent) return null;

    const patientInfo = patientData ? { uid: patientData.uid || 'N/A', fullName: patientData.full_name || 'Unknown Patient' } : { uid: 'N/A', fullName: 'Unknown Patient' };

    const statusLabels = { globalHealth: 'Global Health', painSummary: 'Pain Summary', diseaseActivity: 'Disease Activity', functionalImpact: 'Functional Impact', compliance: 'Compliance', jointPath: 'Joint Assessment', systemic: 'Systemic', gut: 'Gut', psych: 'Psychological', ayurvedic: 'Ayurvedic', currentMedications: 'Current Medications', otherMedications: 'Other Medications', pastMedicalHistory: 'Past Medical History', previousTestsTreatment: 'Previous Tests & Treatment', patientPerspective: 'Patient Perspective (ICE)' };
    let clinicalStatusHtml = '';
    for (const [key, value] of Object.entries(clinicalStatus)) {
      if (value && typeof value === 'string' && value.trim()) {
        const label = statusLabels[key] || key.replace(/([A-Z])/g, ' $1').trim();
        clinicalStatusHtml += `<div class="mb-2"><small class="text-uppercase font-weight-bold text-muted">${label}</small><textarea class="form-control mt-1 ai-edit-field" data-section="clinicalStatus" data-key="${key}" rows="3">${value}</textarea></div>`;
      }
    }

    const holisticLabels = { sleepRoutine: 'Sleep Routine', dietHabits: 'Diet & Habits', stressLifeEvents: 'Stress & Life Events', wellbeingSnapshot: 'Wellbeing Snapshot', substanceUse: 'Substance Use' };
    let holisticHtml = '';
    for (const [key, value] of Object.entries(holistic)) {
      if (value && typeof value === 'string' && value.trim()) {
        const label = holisticLabels[key] || key.replace(/([A-Z])/g, ' $1').trim();
        holisticHtml += `<div class="mb-2"><small class="text-uppercase font-weight-bold text-muted">${label}</small><textarea class="form-control mt-1 ai-edit-field" data-section="holisticDashboard" data-key="${key}" rows="3">${value}</textarea></div>`;
      }
    }

    // VAS
    let vasScore = null;
    const painText = clinicalStatus.painSummary || clinicalStatus.globalHealth || '';
    const vasMatch = painText.match(/(\d+)\s*\/\s*10/);
    if (vasMatch) vasScore = parseInt(vasMatch[1]);
    const vasColor = vasScore !== null ? (vasScore <= 3 ? '#38a169' : vasScore <= 6 ? '#dd6b20' : '#e53e3e') : null;
    const vasLabel = vasScore !== null ? (vasScore <= 3 ? 'Mild' : vasScore <= 6 ? 'Moderate' : 'Severe') : '';

    // PERMA-H
    let permaHtml = '';
    const wsText = holistic.wellbeingSnapshot || '';
    if (wsText && !['Not assessed', 'Not reported', 'Not answered'].includes(wsText.trim())) {
      const ext = (p) => { const m = wsText.match(p); return m ? parseInt(m[1]) : null; };
      const ps = { P: ext(/(?:joy|peace|happy)[^0-9]*(\d+)/i), E: ext(/(?:engag|interested|involved)[^0-9]*(\d+)/i), R: ext(/(?:support|connect)[^0-9]*(\d+)/i), M: ext(/(?:meaning|purpose)[^0-9]*(\d+)/i), A: ext(/(?:confiden|accomplish)[^0-9]*(\d+)/i), H: ext(/(?:health habit|satisfied with health)[^0-9]*(\d+)/i) };
      const valid = Object.entries(ps).filter(([,v]) => v !== null);
      const hasNonZero = valid.some(([,v]) => v > 0);
      if (valid.length > 0 && hasNonZero) {
        const colors = { P:'#e74c3c', E:'#f39c12', R:'#2ecc71', M:'#3498db', A:'#9b59b6', H:'#1abc9c' };
        const labs = { P:'Positive', E:'Engage', R:'Relation', M:'Meaning', A:'Accomplish', H:'Health' };
        const mean = (valid.reduce((s,[,v]) => s+v, 0) / valid.length).toFixed(1);
        const bars = valid.map(([k,v]) => `<div style="display:flex;flex-direction:column;align-items:center;flex:1;"><span style="font-size:11px;font-weight:600;color:#4a5568;margin-bottom:3px;">${v}</span><div style="width:100%;max-width:40px;height:${v*14}px;background:${colors[k]};border-radius:3px 3px 0 0;"></div><span style="font-size:13px;font-weight:700;color:${colors[k]};margin-top:4px;">${k}</span><span style="font-size:8px;color:#a0aec0;">${labs[k]}</span></div>`).join('');
        permaHtml = `<div style="margin-top:14px;padding:14px 18px;background:#fafafa;border:1px solid #eee;border-radius:6px;"><div class="section-title" style="margin-top:0;">Wellbeing Profile <span style="float:right;font-size:12px;color:#718096;font-weight:500;">Mean: ${mean}/10</span></div><div style="display:flex;align-items:flex-end;justify-content:center;gap:10px;height:170px;padding-top:10px;">${bars}</div></div>`;
      }
    }

    // Referrals
    let referralsHtml = '';
    if (referrals.length > 0) {
      const knownDepts = ['Physiotherapist', 'Dietitian', 'Psychologist', 'Ayurvedic'];
      const deptAliases = { 'yoga expert': 'Yoga / Wellness' };
      const extracted = [];
      referrals.forEach(r => {
        const rL = r.toLowerCase();
        const ak = Object.keys(deptAliases).find(a => rL.includes(a));
        if (ak) { extracted.push(deptAliases[ak]); return; }
        const f = knownDepts.find(d => rL.includes(d.toLowerCase()));
        if (f) extracted.push(f);
      });
      if (extracted.length > 0) {
        referralsHtml = '<div class="field-label mt-3">Suggested Referrals</div><div class="mb-3">' + extracted.map(d => '<span style="display:inline-block;padding:4px 12px;margin:2px 4px 2px 0;background:#f5f5f5;border:1px solid #ddd;border-radius:3px;font-size:0.85rem;color:#555;">' + d + '</span>').join('') + '</div>';
      }
    }

    return `
      <style>
        #aiSummaryModal .modal-body { max-height: 80vh; overflow-y: auto; padding: 20px 24px; }
        #aiSummaryModal textarea.form-control { font-size: 1rem !important; line-height: 1.6; overflow: hidden; resize: none; border: 1px solid #ddd; }
        #aiSummaryModal .form-control { font-size: 1rem !important; }
        #aiSummaryModal .section-title { font-size: 1.05rem; font-weight: 700; color: #C9A84C; margin: 18px 0 10px; padding-bottom: 4px; border-bottom: 1px solid #eee; }
        #aiSummaryModal .field-label { font-size: 0.75rem; text-transform: uppercase; font-weight: 700; color: #999; margin-bottom: 3px; }
      </style>
      <script>
        setTimeout(function() {
          $('#aiSummaryModal textarea').each(function() { this.style.height = 'auto'; this.style.height = (this.scrollHeight + 4) + 'px'; }).on('input', function() { this.style.height = 'auto'; this.style.height = (this.scrollHeight + 4) + 'px'; });
        }, 300);
      </script>
      <div class="modal fade" id="aiSummaryModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-xl" role="document">
          <div class="modal-content border-0 shadow">
            <div class="modal-header" style="background: #C9A84C; padding: 14px 24px;">
              <div>
                <span style="font-size: 1.2rem; font-weight: 700; color: white;">Intelligence Summary</span>
                <span style="color: rgba(255,255,255,0.8); margin-left: 15px; font-size: 0.95rem;">${patientInfo.fullName} (${patientInfo.uid})</span>
              </div>
              <div>
                <button type="button" class="btn btn-sm btn-light btn-print-summary mr-2" style="font-size: 0.8rem;"><i class="fa fa-print"></i> Print</button>
                <button type="button" class="close text-white" data-dismiss="modal"><span>&times;</span></button>
              </div>
            </div>
            <div class="modal-body">
              ${redFlags.length > 0 ? `<div style="background:#fff5f5;border:1px solid #fed7d7;border-left:4px solid #e53e3e;border-radius:6px;padding:8px 14px;margin-bottom:14px;">
                <div style="font-size:0.75rem;font-weight:700;color:#c53030;text-transform:uppercase;margin-bottom:4px;"><i class="fa fa-exclamation-triangle mr-1"></i>Red Flags</div>
                <textarea id="ai-edit-red-flags" class="form-control" rows="2" style="font-size:0.9rem;border:1px solid #fed7d7;background:#fff;color:#c53030;">${redFlags.join('; ')}</textarea>
              </div>` : ''}
              ${vasScore !== null ? `<div style="display:flex;align-items:center;margin-bottom:14px;padding:8px 14px;background:#fafafa;border-radius:4px;border:1px solid #eee;"><span style="font-size:0.9rem;font-weight:600;color:#555;">VAS Pain Score</span><span style="font-size:1.4rem;font-weight:700;color:${vasColor};margin-left:12px;">${vasScore}/10</span><span style="font-size:0.8rem;font-weight:600;color:${vasColor};margin-left:8px;">${vasLabel}</span></div>` : ''}
              ${chiefComplaints ? `<div class="field-label">Chief Complaints</div><textarea id="ai-edit-chief-complaint" class="form-control mb-3" rows="2">${chiefComplaints}</textarea>` : ''}
              ${clinicalImpression ? `<div class="field-label">Clinical Impression <span style="font-weight:400;text-transform:none;color:#bbb;">(not printed)</span></div><textarea id="ai-edit-clinical-impression" class="form-control mb-3" rows="4">${clinicalImpression}</textarea>` : ''}
              ${clinicalStatusHtml ? `<div class="row"><div class="col-md-${holisticHtml ? '6' : '12'}"><div class="section-title">Clinical Status</div>${clinicalStatusHtml}</div>${holisticHtml ? `<div class="col-md-6"><div class="section-title">Lifestyle & Wellbeing</div>${holisticHtml}</div>` : ''}</div>` : ''}
              ${permaHtml}
              ${specialistNotes.length > 0 ? `
              <div style="margin-top:14px;">
                <div class="section-title">Specialist Notes</div>
                ${specialistNotes.map((n, i) => `
                  <div style="padding:8px 14px; background:#fafafa; border:1px solid #eee; border-radius:4px; margin-bottom:6px;">
                    <div style="display:flex; justify-content:space-between; align-items:center;">
                      <div style="font-size:0.8rem; font-weight:600; color:#1E9BB7;">${n.department} <span style="font-weight:400; color:#94a3b8;">— ${n.specialist}</span></div>
                      <label style="font-size:0.75rem; color:#64748b; margin:0; cursor:pointer;"><input type="checkbox" class="specialist-note-print-check" data-index="${i}" checked style="margin-right:4px;"> Include in print</label>
                    </div>
                    <div style="font-size:0.9rem; margin-top:3px;">${n.note}</div>
                  </div>`).join('')}
              </div>` : ''}
              ${referralsHtml}
              <p class="text-center mt-2 mb-0" style="font-size:0.8rem;color:#bbb;">This automated summary is for reference only. Clinical judgment should always take precedence.</p>
            </div>
            <div class="modal-footer" style="padding:10px 24px;border-top:1px solid #eee;">
              <button type="button" class="btn btn-confirm px-4 py-2" style="background:#C9A84C;color:white;border:none;border-radius:6px;font-weight:500;"><i class="fa fa-check mr-1"></i> Save & Continue</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  printAiSummary() {
    const patientId = $('#printAiSummaryBtn').attr('data-patient-id');
    if (!patientId) {
      alert('Please select a patient first');
      return;
    }

    const HOST = window.location.origin;
    const TOKEN = window.TOKEN || window.readCookie("_priac_jwt");

    $.ajax({
      url: `${HOST}/quick_check/print_ai_summary?patient_id=${patientId}`,
      headers: { "Authorization": `Token ${TOKEN}` },
      method: 'GET',
      success: function(data) {
        if (data.content) {
          window.printPage(data.content);
        } else {
          alert('No AI summary available to print');
        }
      },
      error: function() {
        alert('Error loading AI summary for printing');
      }
    });
  }

}